import { isAfter } from "date-fns";
import { meanBy } from "lodash-es";

import {
  GLOBAL_SHARED_ACCESS_ID,
  INSTRUCTOR_SHARED_ACCESS_ID,
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  groupAccessIdFromGroupId,
  isGroupAccessId,
  isStudentAccessId,
  studentAccessIdFromUserId,
} from "../../shared/access-id";
import { SectionUserActivityType } from "../../shared/types/attendance";
import { DEFAULT_CODE_KERNEL_LANG } from "../../shared/worksheets/code-kernels";

import {
  activitySelectors,
  assessmentSelectors,
  chatMessageSelectors,
  classSessionSelectors,
  classUserSelectors,
  courseGroupingSelectors,
  courseSelectors,
  createAppSelector,
  pageAccessDataSelectors,
  pageGroupSelectors,
  pageGroupUserSelectors,
  pageOutcomeSelectors,
  pageSelectors,
  sectionSelectors,
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  submissionSelectors,
  termSelectors,
  topicSelectors,
  userSelectors,
} from "./base";
import { selectClassChatReadAtForCurrentSection } from "./local";
import { selectCurrentOrgRole } from "./org";
import {
  Capability,
  UserRoleType,
  hasSectionCapability,
  selectCurrentUserIsMPAdmin,
  selectCurrentUserRole,
  selectUserIdsWhoCanCreateChatMessage,
} from "./permissions";
import {
  selectCurrentSectionUser,
  selectEnabledSectionUsers,
  selectEnabledSectionUsersForCurrentSection,
} from "./section-user";
import { getSortedPages, sortByTermYearRegex, sortTopicActivities } from "./sorting";
import type {
  CourseGroupingWithCoursesType,
  CourseWithGroupingType,
  EnhancedUserWithSectionActivity,
  ObjectWithSections,
  PageWithAssessmentInfoType,
  PageWithTopicIdType,
  SectionWithAssessmentInfoType,
  SectionWithPresentationInfoType,
  UserWithSectionActivity,
  UserWithSectionUserType,
} from "./types";
import { SectionUserActivityStatus } from "./types";
import { selectAdminSectionId, selectIsStudentPreview } from "./view";

import { getTotalSubmissions } from "components/materials/page/assessment/helpers";
import type {
  ActivityType,
  CoreUserRoleType,
  CourseType,
  FullSectionType,
  PageAccessDataType,
  PageCategory,
  PageType,
  SectionRoleType,
  SectionUserType,
  TermType,
  TopicType,
  UserType,
} from "components/server-types";
import { InteractionMode, SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import { DEFAULT_ORDER_FN, ORDER_BY_CODE, ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { RootState } from "store";
import { isGroupPage, isIndividualPage, isStaticPage } from "utils/page-utils";
import {
  CONTENT_SIDEBAR_TAB_NAME,
  HomeRoleType,
  SectionPageCategoryToSidebarTab,
  pageUrl,
  sectionHomeUrl,
} from "utils/urls";
import { getUserFullName, getUserShortName } from "utils/user-utils";

export const selectOrgIdForSectionId = (state: RootState, sectionId: string) =>
  state.auth.section_to_org_id_map?.[sectionId];

export const selectUserNameById = createAppSelector([userSelectors.selectById], (user) =>
  getUserFullName(user),
);

export const selectCurrentSection = createAppSelector(
  [selectCurrentSectionId, sectionSelectors.selectEntities],
  (sectionId, sections) => (sectionId && sectionId in sections ? sections[sectionId] : null),
);

export const selectCurrentSectionIsLinked = createAppSelector(
  [selectCurrentSection],
  (currentSection) => Boolean(currentSection?.kind === SectionKind.LINKED),
);

// Selects the admin section if set, otherwise the current section.
export const selectDesignatedSection = createAppSelector(
  [selectAdminSectionId, selectCurrentSectionId, sectionSelectors.selectEntities],
  (adminSectionId, currentSectionId, sections) => {
    const sectionId = adminSectionId ?? currentSectionId;
    return sectionId && sectionId in sections ? sections[sectionId] : null;
  },
);

export const selectOrgNameBySectionId = createAppSelector(
  [
    (state, sectionId: string) => state.auth.section_to_org_id_map?.[sectionId],
    (state) => state.orgs,
  ],
  (orgId, orgs) => (orgId ? orgs[orgId]?.name || "" : ""),
);

export const selectPageAccessDataForAccessId = createAppSelector(
  [
    sectionUserSelectors.selectAll,
    pageSelectors.selectById,
    pageAccessDataSelectors.filterByPageId,
    (state, pageId: string | null, selectedAccessId: string | null | undefined) => selectedAccessId,
  ],
  (sectionUsers, page, pageAccessData, selectedAccessId) => {
    if (!selectedAccessId || !page) {
      return null;
    }

    if (isGroupAccessId(selectedAccessId)) {
      const groupId = getGroupIdFromAccessId(selectedAccessId);
      return pageAccessData.find((pe) => pe.page_group_id === groupId);
    }

    if (isStudentAccessId(selectedAccessId)) {
      const studentId = getStudentIdFromAccessId(selectedAccessId);
      const sectionUser = sectionUsers.find(
        (cu) => cu.user_id === studentId && cu.section_id === page.section_id,
      );
      if (sectionUser) {
        return pageAccessData.find((pe) => pe.section_user_id === sectionUser.id);
      }
    }

    return pageAccessData?.[0];
  },
);

export const selectPageAccessDataForSectionUser = createAppSelector(
  [
    pageGroupSelectors.filterByPageId,
    pageGroupUserSelectors.selectAll,
    pageSelectors.selectById,
    pageAccessDataSelectors.filterByPageId,
    (state, pageId: string, sectionUserId: string | null) => sectionUserId,
  ],
  (
    pageGroups,
    pageGroupUsers,
    page,
    pageAccessData,
    sectionUserId,
  ): PageAccessDataType | undefined => {
    if (!sectionUserId) {
      return undefined;
    }

    if (isGroupPage(page)) {
      const pageGroupIds = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === sectionUserId && pgu.is_ready)
        .map((pgu) => pgu.page_group_id);

      const groupId = pageGroups.find((pg) => pageGroupIds.includes(pg.id))?.id;

      return pageAccessData.find((data) => data.page_group_id === groupId);
    }

    if (isIndividualPage(page) || isStaticPage(page)) {
      // Static pages are treated as individual pages for the purpose of marking pages complete.
      return pageAccessData.find((data) => data.section_user_id === sectionUserId);
    }

    return pageAccessData?.find(
      (data) => data.page_group_id === null && data.section_user_id === null,
    );
  },
);

export const selectEnabledSectionUsersByRole = createAppSelector(
  [selectEnabledSectionUsers, (state, role?: SectionRoleType) => role],
  (sectionUsers, role) => sectionUsers.filter((cu) => !role || cu.role === role),
);

export const selectCurrentSectionKernelLang = (state: RootState) =>
  selectCurrentSection(state)?.code_blocks_default_language || DEFAULT_CODE_KERNEL_LANG;

export const selectFullSectionsSorted = createAppSelector(
  [sectionSelectors.selectAll, (state) => state.courses],
  (sections, courses): FullSectionType[] =>
    sections
      .map((section) => {
        const course = courses[section.course_id];
        return {
          ...section,
          full_title: t("common.course_code_section_title", {
            course_code: course?.code,
            section_title: section.title,
          }),
        } as FullSectionType;
      })
      .sort((a, b) => a.full_title.localeCompare(b.full_title)),
);

export const selectSectionsForCurrentUser = createAppSelector(
  [
    (state, role: HomeRoleType | undefined) => role,
    sectionSelectors.selectAll,
    sectionUserSelectors.filterForCurrentUser,
    classSessionSelectors.selectAll,
  ],
  (role, sections, sectionUsers, classSessions) => {
    const sectionPresentationMap = classSessions.reduce(
      (acc, cs) => {
        if (cs.presented_page_id) {
          // eslint-disable-next-line no-param-reassign
          acc[cs.section_id] = cs.presented_page_id;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    return sections
      .filter(
        (section) =>
          role === "admin" ||
          sectionUsers.some((cu) => cu.section_id === section.id && cu.role === role),
      )
      .map(
        (section) =>
          ({
            ...section,
            presented_page_id: sectionPresentationMap[section.id] || null,
          }) as SectionWithPresentationInfoType,
      );
  },
);

export const selectSectionsWithAssessablePagesForCurrentUser = createAppSelector(
  [
    (state, role: HomeRoleType | undefined) => role,
    sectionSelectors.selectAll,
    sectionUserSelectors.filterForCurrentUser,
    pageSelectors.selectAll,
    pageOutcomeSelectors.selectAll,
    submissionSelectors.selectAll,
    assessmentSelectors.selectAll,
    (state) => state.courses,
    selectEnabledSectionUsers,
    (state) => state.activities,
    (state) => state.topics,
    pageGroupUserSelectors.selectAll,
    pageGroupSelectors.selectAll,
  ],
  (
    role,
    sections,
    sectionUsersForCurrentUser,
    pages,
    pageOutcomes,
    submissions,
    assessments,
    courseMap,
    enabledSectionUsers,
    activityMap,
    topicMap,
    pageGroupUsers,
    pageGroups,
  ): SectionWithAssessmentInfoType[] => {
    const sectionsForRole = sections.filter(
      (section) =>
        role === "admin" ||
        sectionUsersForCurrentUser.some((cu) => cu.section_id === section.id && cu.role === role),
    );

    return sectionsForRole.map((section) => {
      // TODO: this should be changed to use capabilities
      const assessiblePages = pages.filter((p) => {
        if (p.section_id !== section.id) {
          return false;
        }

        // Don't show unreleased pages, unless the user is an admin or instructor
        if (!p.released_at && !["admin", "instructor"].includes(role)) {
          return false;
        }

        // Only show pages with submissions
        if (!p.submissions_last_collected_at) {
          return false;
        }

        // Students should only see pages where they have an assessed submission
        if (role === "student") {
          if (!p.assessments_published_at) {
            return false;
          }

          const mySectionUser = sectionUsersForCurrentUser.find(
            (cu) => cu.section_id === section.id && cu.role === role,
          );

          if (!mySectionUser) {
            return false;
          }

          const myOfficialSubmission = submissions.find(
            (s) => s.page_id === p.id && s.section_user_id === mySectionUser.id && s.official_at,
          );

          if (!myOfficialSubmission) {
            return false;
          }

          const myAssessment = assessments.find((a) => a.submission_id === myOfficialSubmission.id);
          if (!myAssessment) {
            return false;
          }
        }

        return true;
      });

      const pagesWithAssessmentInfo: PageWithAssessmentInfoType[] = assessiblePages.map((page) => {
        const po = pageOutcomes.filter((pout) => pout.page_id === page.id);
        const submissionsForPage = submissions.filter((s) => {
          if (s.page_id !== page.id) {
            return false;
          }

          // All group submissions are included (filtering out empty groups happens in getTotalSubmissions)
          if (s.page_group_id) {
            return true;
          }

          // For individual submissions, only count those from non-disabled students
          const sectionUser = enabledSectionUsers.find((cu) => cu.id === s.section_user_id);
          return sectionUser && sectionUser.role === "student";
        });

        // Get page group users for this page that have enabled section users
        const pageGroupsForPage = pageGroups.filter((pg) => pg.page_id === page.id);
        const pageGroupIdsForPage = new Set(pageGroupsForPage.map((pg) => pg.id));
        const enabledSectionUserIds = new Set(enabledSectionUsers.map((cu) => cu.id));
        const pageGroupUsersForPage = pageGroupUsers.filter(
          (pgu) =>
            pageGroupIdsForPage.has(pgu.page_group_id) &&
            enabledSectionUserIds.has(pgu.section_user_id),
        );

        const assessedSubmissionIds = new Set(
          submissionsForPage.filter((s) => s.official_at).map((s) => s.id),
        );

        const assessmentsForPage = assessments.filter((a) =>
          assessedSubmissionIds.has(a.submission_id),
        );

        const avgPoints = assessmentsForPage.length
          ? meanBy(assessmentsForPage, (a) => a.points)
          : undefined;

        const activity = page.activity_id ? activityMap[page.activity_id] || null : null;
        const topic = activity?.topic_id ? topicMap[activity.topic_id] || null : null;

        return {
          ...page,
          activity,
          topic,
          page_outcomes: po,
          assessible_submissions_count: getTotalSubmissions(
            page,
            submissionsForPage,
            pageGroupUsersForPage,
          ),
          assessed_submissions_count: assessmentsForPage.length,
          average_points: avgPoints,
        };
      });

      return {
        ...section,
        course: courseMap[section.course_id],
        pages: pagesWithAssessmentInfo,
      };
    });
  },
);

export const selectCurrentCourseCode = createAppSelector(
  [selectCurrentSection, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.code) || null,
);

export const selectCourseCodeBySectionId = createAppSelector(
  [sectionSelectors.selectById, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.code) || null,
);

export const selectLmsSectionId = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.lms_section_id || null,
);

export const selectCurrentSectionTitle = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.title || null,
);

export const selectSectionTitleBySectionId = createAppSelector(
  [sectionSelectors.selectById],
  (section) => section?.title || null,
);

export const selectCurrentSectionDescription = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.description || null,
);

export const selectCurrentSectionAvEnabled = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.av_enabled || false,
);

export const selectCurrentSectionAllowStudentsToJoinAv = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.allow_students_to_join_av || false,
);

const _mapUserToSectionUsers = (
  sectionUsers: SectionUserType[],
  usersMap: Record<string, UserType>,
): UserWithSectionUserType[] => {
  const sectionUsersWithNames = sectionUsers.map((sectionUser) => {
    const user = usersMap[sectionUser.user_id];
    return {
      section_user_id: sectionUser.id,
      ...sectionUser,
      ...user,
    };
  });

  return sectionUsersWithNames;
};

// TODO: Put these two in a more appropriate place.
export const determineActivityStatus = (
  activity: SectionUserActivityType,
): SectionUserActivityStatus => {
  if (!activity) {
    return SectionUserActivityStatus.OFFLINE;
  }

  return activity.active === "0"
    ? SectionUserActivityStatus.INACTIVE
    : SectionUserActivityStatus.ACTIVE;
};

const determineCurrentPageId = (activity: SectionUserActivityType): string | null => {
  if (!activity) {
    return null;
  }

  return activity.pageId;
};

export const selectActivityStatus = (state: RootState) => state.attendance.sectionUserActivities;

export const selectActivityStatusForUser = (state: RootState, userId: string) =>
  determineActivityStatus(state.attendance.sectionUserActivities[userId]);

export const selectFollowingStatusForUser = (state: RootState, userId: string) =>
  state.attendance.sectionUserActivities[userId]?.following === "1" || false;

export const selectActivePageIdForUser = (state: RootState, userId: string) =>
  determineCurrentPageId(state.attendance.sectionUserActivities[userId]);

export const selectUsersWithSectionActivityInCurrentSection = createAppSelector(
  [
    sectionUserSelectors.filterForCurrentSection,
    userSelectors.selectEntities,
    (state) => state.attendance.sectionUserActivities,
  ],
  (sectionUsers, users, sectionUserActivities): UserWithSectionActivity[] =>
    _mapUserToSectionUsers(sectionUsers, users).map((user) => ({
      ...user,
      sectionActivityStatus: determineActivityStatus(sectionUserActivities[user.user_id]),
      isFollowing: sectionUserActivities[user.user_id]?.following === "1" || false,
      pageId: determineCurrentPageId(sectionUserActivities[user.user_id]),
    })),
);

export const selectUserIdsInCurrentSection = createAppSelector(
  [selectEnabledSectionUsersForCurrentSection],
  (sectionUsers) => sectionUsers.map((cu) => cu.user_id),
);

export const selectClassUserByUserIdMap = createAppSelector(
  [classUserSelectors.filterForCurrentSection],
  (classUsers) => new Map(classUsers.map((classUser) => [classUser.user_id, classUser])),
);

// NOTE: this is used in the SectionUsersSidebarPanel where we need to sort a list of users by
// section activity status and by specific fields on the ClassUser model. This could be refactored
// in the future to be more elegant/efficient, but it works for now.
export const selectEnhancedUsersWithSectionActivityInCurrentSection = createAppSelector(
  [
    sectionUserSelectors.filterForCurrentSection,
    selectClassUserByUserIdMap,
    userSelectors.selectEntities,
    (state) => state.attendance.sectionUserActivities,
  ],
  (sectionUsers, classUserMap, users, sectionUserActivities): EnhancedUserWithSectionActivity[] =>
    _mapUserToSectionUsers(sectionUsers, users).map((user) => ({
      ...user,
      sectionActivityStatus: determineActivityStatus(sectionUserActivities[user.user_id]),
      isFollowing: sectionUserActivities[user.user_id]?.following === "1" || false,
      pageId: determineCurrentPageId(sectionUserActivities[user.user_id]),
      hand_raised_at: classUserMap.get(user.user_id)?.hand_raised_at || null,
      is_in_class_session: classUserMap.get(user.user_id)?.is_in_class_session || false,
    })),
);

export const selectUsersInCurrentSection: (state: RootState) => UserWithSectionUserType[] =
  createAppSelector(
    [selectEnabledSectionUsersForCurrentSection, userSelectors.selectEntities],
    (sectionUsers, users): UserWithSectionUserType[] => _mapUserToSectionUsers(sectionUsers, users),
  );

export const selectUserCountInCurrentSection = createAppSelector(
  [selectEnabledSectionUsersForCurrentSection],
  (sectionUsers) => sectionUsers.length,
);

export const selectOnlineUserCountInCurrentSection = createAppSelector(
  [selectUsersWithSectionActivityInCurrentSection],
  (users) =>
    users?.filter((user) => user.sectionActivityStatus !== SectionUserActivityStatus.OFFLINE)
      .length || 0,
);

export const selectSortedTermSectionsByCourse = createAppSelector(
  [(state, courseId: string) => courseId, termSelectors.selectAll, sectionSelectors.selectAll],
  (courseId, terms, sections) => {
    const filteredSections = sections.filter((section) => section.course_id === courseId);

    const termsWithSections = terms.map<ObjectWithSections<TermType>>((term) => {
      const itemSections = filteredSections.filter((section) => section.term_id === term.id);
      const sortedSections = itemSections.toSorted(ORDER_BY_TITLE);
      const sortedSectionsWithOrder = sortedSections.map((section, index) => ({
        ...section,
        order: index,
      }));
      return { ...term, sections: sortedSectionsWithOrder };
    });

    return termsWithSections.sort(sortByTermYearRegex);
  },
);

export const selectCurrentTerm = createAppSelector(
  [selectCurrentSection, (state) => state.terms],
  (currentSection, terms) => (currentSection?.term_id && terms[currentSection?.term_id]) || null,
);

export const selectCurrentTermTitle = createAppSelector(
  [selectCurrentTerm],
  (currentTerm) => currentTerm?.title || null,
);

export const selectSortedCoursesWithSectionsByTerm = createAppSelector(
  [(state, termId: string) => termId, courseSelectors.selectAll, sectionSelectors.selectAll],
  (termId, courses, sections): ObjectWithSections<CourseType>[] => {
    const filteredSections = sections.filter((section) => section.term_id === termId);

    const coursesWithSections = courses.map<ObjectWithSections<CourseType>>((course) => {
      const itemSections = filteredSections.filter((section) => section.course_id === course.id);
      const sortedSections = itemSections.toSorted(ORDER_BY_TITLE);
      const sortedSectionsWithOrder = sortedSections.map((section, index) => ({
        ...section,
        order: index,
      }));
      return { ...course, sections: sortedSectionsWithOrder };
    });

    return coursesWithSections.sort(ORDER_BY_CODE);
  },
);

export type GroupingWithCoursesAndSectionsType = {
  id: string | null;
  title: string;
  courses: ObjectWithSections<CourseType>[];
};

/**
 * Returns groupings with their courses and sections for a given term.
 * Includes a "fake" grouping (id: null) for ungrouped courses.
 * All groupings, courses, and sections are sorted by title.
 */
export const selectGroupingsWithCoursesByTerm = createAppSelector(
  [
    (state, termId: string) => selectSortedCoursesWithSectionsByTerm(state, termId),
    (state) => state.course_groupings,
  ],
  (coursesWithSections, groupings): GroupingWithCoursesAndSectionsType[] => {
    // Group courses by grouping_id
    const groupedCourses: Record<string, ObjectWithSections<CourseType>[]> = {};
    const ungroupedCourses: ObjectWithSections<CourseType>[] = [];

    coursesWithSections.forEach((course) => {
      if (course.sections.length === 0) {
        return;
      }

      if (course.grouping_id) {
        if (!groupedCourses[course.grouping_id]) {
          groupedCourses[course.grouping_id] = [];
        }

        groupedCourses[course.grouping_id].push(course);
      } else {
        ungroupedCourses.push(course);
      }
    });

    // Build result with real groupings
    const result: GroupingWithCoursesAndSectionsType[] = Object.entries(groupedCourses).map(
      ([id, courses]) => ({
        id,
        title: groupings[id]?.title || "",
        courses: courses.sort(ORDER_BY_CODE),
      }),
    );

    // Add fake grouping for ungrouped courses
    if (ungroupedCourses.length > 0) {
      result.push({
        id: null,
        title: "",
        courses: ungroupedCourses.sort(ORDER_BY_CODE),
      });
    }

    // Sort groupings by title, but keep uncategorized (null id) at the end
    return result.sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;

      // eslint-disable-next-line babel/new-cap
      return ORDER_BY_TITLE(a, b);
    });
  },
);

export const selectCourseSectionsByTerms = createAppSelector(
  [termSelectors.selectAll, courseSelectors.selectAll, sectionSelectors.selectAll],
  (terms, courses, sections) => {
    return terms.reduce(
      (acc, term) => {
        const filteredSections = sections.filter((section) => section.term_id === term.id);
        const coursesWithSections = courses.map<ObjectWithSections<CourseType>>((course) => {
          const itemSections = filteredSections
            .filter((section) => section.course_id === course.id)
            .map((section, index) => ({ ...section, order: index }));
          return { ...course, sections: itemSections };
        });
        // eslint-disable-next-line no-param-reassign
        acc[term.id] = coursesWithSections;
        return acc;
      },
      {} as Record<string, ObjectWithSections<CourseType>[]>,
    );
  },
);

export const selectCourseGroupingsWithCourses = createAppSelector(
  [courseGroupingSelectors.selectAll, courseSelectors.selectAll],
  (groupings, courses) => {
    // Create a map of grouping_id to courses
    const coursesByGrouping = new Map<string, CourseType[]>();
    courses.forEach((course) => {
      if (course.grouping_id) {
        const existing = coursesByGrouping.get(course.grouping_id) || [];
        coursesByGrouping.set(course.grouping_id, [...existing, course]);
      }
    });

    // Map groupings to include their courses and sort by title
    return groupings
      .map<CourseGroupingWithCoursesType>((grouping) => ({
        ...grouping,
        courses: coursesByGrouping.get(grouping.id)?.sort(ORDER_BY_CODE) || [],
      }))
      .sort(ORDER_BY_TITLE);
  },
);

export const selectUngroupedCourses = createAppSelector([courseSelectors.selectAll], (courses) =>
  courses.filter((course) => !course.grouping_id).sort(ORDER_BY_CODE),
);

export const selectCoursesByGroupingId = createAppSelector(
  [(state, groupingId: string | null) => groupingId, courseSelectors.selectAll],
  (groupingId, courses) =>
    courses.filter((course) => course.grouping_id === groupingId).sort(ORDER_BY_CODE),
);

export const selectCoursesWithGrouping = createAppSelector(
  [courseSelectors.selectAll, (state) => state.course_groupings],
  (courses, groupings): CourseWithGroupingType[] => {
    // Create a map of grouping_id to grouping title for quick lookup
    return courses.map((course) => ({
      ...course,
      groupingTitle:
        course.grouping_id && course.grouping_id in groupings
          ? groupings[course.grouping_id].title
          : null,
    }));
  },
);

export const selectAuthorizedPages = createAppSelector(
  [
    pageSelectors.selectAll,
    selectCurrentUserRole,
    selectEnabledSectionUsersByRole,
    selectCurrentUserId,
    selectCurrentSectionId,
    (_state, role?: CoreUserRoleType) => role,
  ],
  (pages, currentSectionUserRole, sectionUsers, userId, currentSectionId, role) => {
    // TODO: make make this a helper function
    const userRole =
      role === "student"
        ? UserRoleType.STUDENT
        : role === "instructor"
          ? UserRoleType.INSTRUCTOR
          : currentSectionUserRole;

    const sectionIds = role
      ? sectionUsers.filter((cu) => cu.user_id === userId).map((cu) => cu.section_id)
      : [currentSectionId];

    const canAuthorSection = hasSectionCapability(userRole, Capability.CAN_AUTHOR_SECTION);
    return pages.filter(
      (page) => sectionIds.includes(page.section_id) && (canAuthorSection || page.released_at),
    );
  },
);

export const selectPagesForCurrentSection = createAppSelector(
  [(state, category?: PageCategory) => category, (state) => selectAuthorizedPages(state)],
  (category, pages): PageType[] => pages.filter((p) => !category || p.category === category),
);

export const selectGroupPagesForCurrentSection = createAppSelector(
  [selectPagesForCurrentSection, activitySelectors.selectAll, topicSelectors.selectAll],
  (pages, activities, topics) => {
    // Filter out individual pages and only keep group pages.
    const groupPages = pages.filter((p) => isGroupPage(p));

    const topicById = topics.reduce(
      (acc, topic) => {
        return { [topic.id]: topic, ...acc };
      },
      {} as Record<string, TopicType>,
    );

    const activityById = activities.reduce(
      (acc, activity) => {
        return { [activity.id]: activity, ...acc };
      },
      {} as Record<string, ActivityType>,
    );

    return groupPages.map((page) => {
      if (page.activity_id) {
        return {
          ...page,
          topic_id: topicById[activityById[page.activity_id]?.topic_id]?.id || undefined,
        } as PageWithTopicIdType;
      }

      return page as PageWithTopicIdType;
    });
  },
);

export const selectSectionsByTermAndCourse = createAppSelector(
  [
    (state, termId: string | null) => termId,
    (state, termId: string | null, courseId: string | null) => courseId,
    sectionSelectors.selectAll,
  ],
  (termId, courseId, sections) =>
    sections
      .filter(
        (section) =>
          (!termId || section.term_id === termId) && (!courseId || section.course_id === courseId),
      )
      .sort(ORDER_BY_TITLE),
);

export type StudentAccessIdInfoType = {
  name: string;
  accessId: string;
};

// TODO: Some of this logic is duplicated in calculateAccessId
// This function determines what access ids should be used for the current page
// If the user is a student it will return that student's access id and user name for the page.
// If the user is not a student it will determine which user has been selected or if all
// the sections users should be used and return those access ids with corresponding names.
export const selectUserDocInfoByPageId = createAppSelector(
  [
    pageSelectors.selectById,
    (state, pageId: string, accessId: string) => accessId,
    (state, pageId: string, accessId: string, isAssessing: boolean) => isAssessing,
    pageGroupSelectors.filterByPageId,
    pageGroupUserSelectors.selectAll,
    selectCurrentSectionUser,
    selectEnabledSectionUsersForCurrentSection,
    userSelectors.selectEntities,
  ],
  (
    page,
    accessId,
    isAssessing,
    groupsForPage,
    pageGroupUsers,
    currentSectionUser,
    sectionUsers,
    users,
  ): StudentAccessIdInfoType[] | null => {
    const shouldRestrictAccess = ["student", "recording_agent"].includes(currentSectionUser?.role);

    if (!page || !sectionUsers?.length) {
      return null;
    }

    if (page.interaction_mode === InteractionMode.SHARED_CLASS) {
      return [{ name: t("common.shared_class"), accessId: GLOBAL_SHARED_ACCESS_ID }];
    }

    let groupIndex = -1;
    let userId: string | null = null;
    if (shouldRestrictAccess) {
      const groupIdsForStudent = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === currentSectionUser?.id && pgu.is_ready)
        .map((pgu) => pgu.page_group_id);
      groupIndex = groupsForPage.findIndex((pg) => groupIdsForStudent.includes(pg.id));

      if (groupIndex === -1) {
        userId = currentSectionUser?.user_id || null;
      }

      // Here an instructor has selected a particular accessId
    } else if (accessId && accessId !== INSTRUCTOR_SHARED_ACCESS_ID) {
      if (isGroupAccessId(accessId)) {
        const groupId = getGroupIdFromAccessId(accessId);
        groupIndex = groupsForPage.findIndex((pg) => pg.id === groupId);
      } else {
        userId = getStudentIdFromAccessId(accessId);
      }
    }

    if (groupIndex > -1) {
      return [
        {
          name: t("common.group_title", { number: groupIndex + 1 }),
          accessId: groupAccessIdFromGroupId(groupsForPage[groupIndex].id),
        },
      ];
    }

    if (userId) {
      const sectionUser = sectionUsers.find((cu) => cu.user_id === userId);
      return sectionUser
        ? [
            {
              // If we are assessing we don't want to show the user's name
              name: isAssessing
                ? "Anonymous Student"
                : getUserShortName(users[sectionUser.user_id]),
              accessId: studentAccessIdFromUserId(sectionUser.user_id),
            },
          ]
        : null;
    }

    // If no user or group selected we need to return everyone
    if (groupsForPage.length) {
      return groupsForPage.map((pg, index) => ({
        name: t("common.group_title", { number: index + 1 }),
        accessId: groupAccessIdFromGroupId(pg.id),
      }));
    }

    return sectionUsers
      .filter((cu) => cu.role === "student")
      .map((cu) => ({
        name: getUserShortName(users[cu.user_id]),
        accessId: studentAccessIdFromUserId(cu.user_id),
      }));
  },
);

export const selectSectionsThatCanBeAuthored = createAppSelector(
  [
    selectIsStudentPreview,
    selectFullSectionsSorted,
    sectionUserSelectors.filterForCurrentUser,
    selectCurrentOrgRole,
    selectCurrentUserIsMPAdmin,
  ],
  (isStudentPreview, sections, sectionUsers, orgRole, isMpAdmin) => {
    if (isStudentPreview) {
      return [];
    }
    return sections.filter((section) => {
      const sectionUser = sectionUsers.find((cu) => cu.section_id === section.id);
      if (sectionUser) {
        return !["student", "recording_agent"].includes(sectionUser.role);
      }
      if (orgRole) {
        return !["other", "recording_agent"].includes(orgRole);
      }
      return isMpAdmin;
    });
  },
);

// When deleting the page the user is currently on, we need to redirect to another page.
// This selector provides the URL to redirect to.
// - If the page we are deleting is part of a topic (whether it has a due date or not), we redirect to the previous
//   page in the same topic. If the page was the first page of the topic, we redirect to the next one within the same
//   topic. We try to stay within the same activity/subcategory (prep work, additional resources, etc.) but if
//   there are no other pages in that category, we will redirect to the previous or next page within the same topic.
// - If the page is an assignment (but not part of a topic), course resource or private workspace page, we redirect to
//   the previous page within the same section. If the page was the first page of the section, we redirect to the next
//   one within the same section.
// - If there are no page left within that topic or section, we redirect to the section page.
export const selectRedirectUrlForPageDeletion = createAppSelector(
  [
    (state, currentPageId: string | undefined) => currentPageId,
    (state, currentPageId: string | undefined, targetPageId: string | undefined) => targetPageId,
    selectCurrentSectionId,
    selectCurrentUserRole,
    pageSelectors.selectById,
    activitySelectors.selectAll,
    pageSelectors.filterForCurrentSection,
  ],
  (currentPageId, targetPageId, currentSectionId, userRole, page, activities, pages) => {
    const canAuthorSection = hasSectionCapability(userRole, Capability.CAN_AUTHOR_SECTION);
    if (
      !canAuthorSection ||
      !currentSectionId ||
      !currentPageId ||
      currentPageId !== targetPageId ||
      !page
    ) {
      return null;
    }
    const SECTION_OVERVIEW_URL = sectionHomeUrl(currentSectionId);
    let sortedPages: PageType[] = [];
    if (page.category === "activity_page") {
      const activity = activities.find((a) => a.id === page.activity_id);
      const topicId = activity?.topic_id;
      if (!topicId) {
        return SECTION_OVERVIEW_URL;
      }
      const sortedActivities = sortTopicActivities(topicId, activities, pages, canAuthorSection);
      sortedPages = getSortedPages(sortedActivities);
    } else {
      sortedPages = pages.filter((p) => p.category === page.category).sort(DEFAULT_ORDER_FN);
    }
    const currentPageIndex = sortedPages.findIndex((p) => p.id === currentPageId);
    if (currentPageIndex === -1) {
      return SECTION_OVERVIEW_URL;
    }
    // If at all possible, we try to stay within the same activity/subcategory
    const previousPage = sortedPages[currentPageIndex - 1];
    const nextPage = sortedPages[currentPageIndex + 1];
    // sortedPages is either empty or only contains the current page.
    if (!previousPage && !nextPage) {
      return SECTION_OVERVIEW_URL;
    }
    // For the first page of sortedPages, we always redirect to the next page.
    if (!previousPage) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, nextPage.id, { tab });
    }
    // For the last page of sortedPages, we always redirect to the previous page.
    if (!nextPage) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, previousPage.id, { tab });
    }
    // If the previous page is part of the same activity/subcategory, we redirect to it.
    if (previousPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, previousPage.id, { tab });
    }
    // If the next page is part of the same activity/subcategory, we redirect to it.
    if (nextPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, nextPage.id, { tab });
    }
    // If we can't stay within the same activity/subcategory, we redirect to the previous page.
    const tab = SectionPageCategoryToSidebarTab[previousPage.category] as CONTENT_SIDEBAR_TAB_NAME;
    return pageUrl(currentSectionId, previousPage.id, { tab });
  },
);

export const selectSortedChatMessagesForCurrentSection = createAppSelector(
  [chatMessageSelectors.filterForCurrentSection, selectUserIdsWhoCanCreateChatMessage],
  (chatMessages, userIdsWhoCanCreateChatMessage) =>
    chatMessages.filter((message) => userIdsWhoCanCreateChatMessage.includes(message.author_id)),
);

export const selectUnreadChatMessageCountForCurrentSection = createAppSelector(
  [selectSortedChatMessagesForCurrentSection, selectClassChatReadAtForCurrentSection],
  (chatMessages, readAt) => {
    if (!readAt) {
      return chatMessages.length;
    }
    return chatMessages.filter((message) => isAfter(message.created_at, readAt)).length;
  },
);
